// @flow
/** @jsx isolateComponent */

import _ from 'lodash';
import { Component } from 'react';

import { StaticImage, StaticVideo } from '@zapier/common-components';
import { isolateComponent, labelStyles } from '@zapier/common-utils';

import Button from 'app/common/components/Button';
import ButtonGroup from 'app/common/components/ButtonGroup';
import ConfettiStateful from 'app/common/components/ConfettiStateful';
import Modal from 'app/common/components/Modal';
import Questions from 'app/team-guide/components/Questions';
import StyledLink from 'app/team-guide/components/StyledLink';
import {
  CanInviteMembers,
  FAQ,
  FAQObject,
  ViewTypes,
} from 'app/team-guide/utils/FAQ';
import { GUIDE_VIEW, WELCOME_VIEW } from 'app/team-guide/utils/constants';
import {
  InfoBlock,
  InfoBlockDescription,
  InfoBlockFooter,
  InfoBlockIconWrap,
  InfoBlockTitle,
} from 'app/common/components/InfoBlock';

const Styles = labelStyles(__filename, {
  questionVideo: {
    marginLeft: '-40px', // pull it to the left edge
    width: 'calc(100% + 80px)', // make it 100% wide plus 2 * 40px for the padding
  },
  guideButton: {
    marginBottom: '0.6em',
    maxWidth: '440px',
  },
});

type TeamsGuideModalProps = {
  style: {},
  view: $Values<typeof ViewTypes>,
  onClose: () => void,
  onSelectView: (view: string) => void,
};

export const TeamsGuideModal = ({
  view,
  style,
  onSelectView,
  onClose,
}: TeamsGuideModalProps) => {
  const renderAnswer = selectedView => {
    const { question, answer, mediaUrl, key } = FAQObject[selectedView];

    return {
      confetti: false,
      imageUrl: false,
      title: question,
      description: () => (
        <InfoBlockDescription kind={key === CanInviteMembers.key && 'centered'}>
          {answer}
          {!_.isEmpty(mediaUrl) && (
            <StaticVideo
              autoPlay={true}
              controls={true}
              css={Styles.questionVideo}
              loop={true}
              src={mediaUrl}
              width="500"
            />
          )}
        </InfoBlockDescription>
      ),
      footer: () => (
        <InfoBlockFooter>
          <Button
            kind="xlarge team min-width"
            onClick={() => onSelectView(GUIDE_VIEW)}
          >
            Back
          </Button>
        </InfoBlockFooter>
      ),
    };
  };

  const renderWelcomeContent = {
    confetti: true,
    imageUrl: '/images/team-guide/illustrations/celebrate.svg',
    title: 'Welcome to Zapier for Teams!',
    description: () => (
      <InfoBlockDescription kind="centered">
        <p>
          Let’s get started. You and your team can now use Zapier for Teams for
          free for 14 days. Explore our guide or jump right into your dashboard.
        </p>
      </InfoBlockDescription>
    ),
    footer: () => (
      <InfoBlockFooter>
        <ButtonGroup kind="horizontal">
          <Button kind="xlarge secondary" onClick={onClose}>
            View Zaps
          </Button>
          <Button kind="xlarge team" onClick={() => onSelectView(GUIDE_VIEW)}>
            Open Guide
          </Button>
        </ButtonGroup>
      </InfoBlockFooter>
    ),
  };

  const renderGuideContent = {
    confetti: false,
    imageUrl: '/images/team-guide/illustrations/wave.svg',
    title: 'Getting Started With Zapier for Teams',
    description: () => (
      <InfoBlockDescription kind="centered">
        <Questions
          css={Styles.guideButton}
          onSelect={onSelectView}
          questions={FAQ}
        />
      </InfoBlockDescription>
    ),
    footer: () => (
      <InfoBlockFooter>
        <p>
          More questions? View our{' '}
          <StyledLink href="/help/account/teams">documentation</StyledLink>.{' '}
          <br />
          New to Zapier? Read our{' '}
          <StyledLink href="/learn/getting-started-guide">
            Getting Started
          </StyledLink>{' '}
          guide.
        </p>
      </InfoBlockFooter>
    ),
  };

  const renderContent = () => {
    const content = () => {
      if (view === WELCOME_VIEW) {
        return renderWelcomeContent;
      } else if (view === GUIDE_VIEW) {
        return renderGuideContent;
      }

      return renderAnswer(view);
    };

    const { confetti, imageUrl, title, description, footer } = content();

    return (
      <InfoBlock kind="bare">
        {confetti && <ConfettiStateful />}
        {imageUrl && (
          <InfoBlockIconWrap>
            <StaticImage alt="Shared Folder" src={imageUrl} />
          </InfoBlockIconWrap>
        )}
        <InfoBlockTitle kind="smaller">{title}</InfoBlockTitle>
        {description()}
        {footer()}
      </InfoBlock>
    );
  };

  return (
    <Modal
      closeOnEsc={true}
      closeOnOutsideClick={false}
      contentClassName={'__content--smaller'}
      isOpened={true}
      isSelfClosing={true}
      kind="overflow-hidden"
      onClose={onClose}
      style={style}
    >
      <div>{renderContent()}</div>
    </Modal>
  );
};

// Export a class so it can be used as a `ref`
export default class TeamsGuideModalRefable extends Component<TeamsGuideModalProps> {
  render() {
    return <TeamsGuideModal {...this.props} />;
  }
}
